import React from 'react';
import Header from './component/Header';
import PublicFooter from '../components/layout/PublicFooter';
import StaticPageLayout from '../components/layout/StaticPageLayout';
import { Button, Accordion } from '../components/ui';
import SEO from '../components/SEO';

const faqData = [
    {
        category: 'Getting Started',
        questions: [
            {
                q: 'What is Renew My Tech?',
                a: 'Renew My Tech (RMT) is an automated vehicle plate renewal service. Once you register your license plate and set up a payment method, we handle your annual registration renewal automatically — no more trips to the DMV or missed deadlines.',
            },
            {
                q: 'How do I sign up?',
                a: 'Click "Sign Up" on our home page and follow the 3-step process: enter your plate details, provide any state-specific information, and set up your billing and payment method. Your account is created automatically when you complete the process.',
            },
            {
                q: 'Which states are supported?',
                a: 'We currently support automated plate renewal in Tennessee. Additional states are being added — check back for updates or contact us to request your state.',
            },
            {
                q: 'Can I add multiple vehicles?',
                a: 'Yes! You can add as many plates as you like to a single account. Each plate can have its own preferred payment method and notification contact.',
            },
        ],
    },
    {
        category: 'Renewal Process',
        questions: [
            {
                q: 'How does automatic renewal work?',
                a: 'Once your plate is registered with us, we monitor the expiration date. About 90 days before expiration, we collect the renewal cost from the state. You receive a notification with the amount, and approximately 3 weeks before expiration, we charge your payment method and submit the renewal on your behalf.',
            },
            {
                q: 'When will I be charged?',
                a: 'You will be charged approximately 3 weeks before your plate expires. We send you a notification with the exact amount about 30 days before expiration, giving you time to review or make changes.',
            },
            {
                q: 'What if the charge fails?',
                a: 'If a charge fails (insufficient funds, expired card, etc.), we notify you and retry the next day. We make up to 3 attempts. After 3 failures, auto-renewal is paused and you will need to update your payment method and reactivate the plate on your dashboard.',
            },
            {
                q: 'How do I receive my new tags?',
                a: 'After your renewal is processed by the state, new registration tags are mailed to the address on your account. Make sure your address is up to date in your profile.',
            },
            {
                q: 'What if I already renewed manually at the DMV?',
                a: 'If you manually renew your plate after registering with RMT, please deactivate that plate on your dashboard immediately to avoid a duplicate charge. If a charge has already been initiated, contact our support team.',
            },
            {
                q: 'Can I skip a renewal year?',
                a: 'You can deactivate auto-renewal for any plate at any time by toggling it to "Inactive" on your dashboard. When you are ready to resume, simply toggle it back to "Active."',
            },
        ],
    },
    {
        category: 'Payments & Fees',
        questions: [
            {
                q: 'What payment methods do you accept?',
                a: 'We accept ACH/eCheck (direct bank transfer), debit cards, and credit cards. You can have multiple payment methods on your account and assign different ones to different plates.',
            },
            {
                q: 'What are the service fees?',
                a: 'Service fees depend on your payment method: ACH/eCheck is $5.00 per renewal, Debit Card is $5.50 per renewal, and Credit Card is $6.50 plus 1.75% of the renewal amount. These fees cover payment processing and the convenience of automated renewal.',
            },
            {
                q: 'Is the renewal cost the same as at the DMV?',
                a: "Yes. The renewal cost comes directly from the state's vehicle registration system and includes all standard state and county fees. The only additional charge is our service fee.",
            },
            {
                q: 'Can I get a refund?',
                a: 'If a renewal has not yet been submitted to the state, contact our support team to discuss your options. Once a renewal has been submitted and processed, the state renewal fee is non-refundable.',
            },
            {
                q: 'How do I change my default payment method?',
                a: 'Go to Payment Methods in your account menu. Toggle the "Make Default" switch on the method you want to use. You can also assign specific payment methods to individual plates from your dashboard.',
            },
        ],
    },
    {
        category: 'Account & Notifications',
        questions: [
            {
                q: 'How do I update my address?',
                a: 'Go to Profile in your account menu. You can update your name and password there. For address changes, go to "Saved Contacts" to update or add a new contact with your current address.',
            },
            {
                q: 'What notifications will I receive?',
                a: 'You will receive notifications for: upcoming charge (about 30 days before expiration), successful payment confirmation, failed payment alerts, renewal completion, and any issues that need your attention. Notifications are sent via email and/or SMS based on your contact preferences.',
            },
            {
                q: 'Can I change how I receive notifications?',
                a: 'Yes. Go to Profile > Saved Contacts to manage your notification methods (email, phone, address). Each plate can be assigned a different preferred contact. You must keep at least one contact on your account.',
            },
            {
                q: 'I forgot my password. How do I reset it?',
                a: 'Click "Sign In" then "Forgot Password." Enter your email address and we will send you a reset link. Click the link in the email to set a new password.',
            },
        ],
    },
];

const Faq = () => {
    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.flatMap(category =>
            category.questions.map(item => ({
                "@type": "Question",
                "name": item.q,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": item.a
                }
            }))
        )
    };

    return (
        <div style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
            <SEO
                title="FAQ - Frequently Asked Questions"
                description="Get answers about RMT's automated vehicle tag renewal service. Learn about pricing, payment methods, renewal process, notifications, and account management."
                canonical="/faq"
                structuredData={faqSchema}
            />
            <Header />
            <StaticPageLayout
                title="Frequently Asked Questions"
                subtitle="Find answers to common questions about our automated vehicle plate renewal service."
            >
                <div className="tw-space-y-8">
                    {faqData.map((section, idx) => (
                        <div key={idx}>
                            <h2 className="tw-text-2xl tw-text-primary-600 tw-m-0 tw-mb-4 tw-pb-3 tw-border-b-2 tw-border-primary-100">
                                {section.category}
                            </h2>
                            <Accordion>
                                {section.questions.map((item, qIdx) => (
                                    <Accordion.Item key={qIdx} title={item.q}>
                                        {item.a}
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        </div>
                    ))}

                    {/* CTA */}
                    <div className="tw-text-center tw-bg-gray-50 tw-rounded-2xl tw-p-8 tw-mt-12">
                        <h3 className="tw-text-2xl tw-text-gray-900 tw-m-0 tw-mb-2">Still have questions?</h3>
                        <p className="tw-text-sm tw-text-gray-500 tw-mb-6 tw-m-0">
                            Our support team is here to help. Reach out and we'll get back to you as soon as possible.
                        </p>
                        <Button variant="primary" to="/contact/us">
                            Contact Us
                        </Button>
                    </div>
                </div>
            </StaticPageLayout>
            <PublicFooter />
        </div>
    );
};

export default Faq;
